<script>
export default {
    name: "ViewTokenContent",
};
</script>

<template>
    <div class="container">
        <div class="px-2 py-3 text-justify">
            <div class="my-auto mx-2">
                <h1 class="text-9 text-black mb-4">
                    ZESA View Token — Check Your Token Purchase History
                </h1>
                <p class="text-3">
                    Lost your ZESA token? Can't remember if you already bought electricity this month? With the Magetsi ZESA View Token tool, you can instantly retrieve your complete ZESA token purchase history by simply entering your meter number. No login required, no phone calls, no stress — just enter your meter number above and see every token you've purchased through Magetsi, right here.
                </p>
            </div>
        </div>

        <div class="px-2 py-3 text-justify">
            <div class="my-auto mx-2">
                <h2 class="text-7 text-black mb-4">
                    How To View Your ZESA Token on Magetsi
                </h2>
                <p class="text-3">
                    Viewing your ZESA tokens is incredibly simple. Here's how it works:
                </p>
                <ol class="text-3">
                    <li class="py-1">
                        Enter your ZESA prepaid meter number in the tool above.
                    </li>
                    <li class="py-1">
                        Click <strong>"View My Tokens"</strong> — your full token history loads instantly.
                    </li>
                    <li class="py-1">
                        See the token number, amount paid, units received, and the date of every purchase you've made on Magetsi.
                    </li>
                </ol>
                <p class="text-3">
                    It's that easy. No account needed, no passwords to remember. Your purchase history is always accessible, 24/7.
                </p>
            </div>
        </div>

        <div class="px-2 py-3 text-justify">
            <div class="my-auto mx-2">
                <h2 class="text-7 text-black mb-4">
                    Why Your ZESA Token History Matters
                </h2>
                <p class="text-3">
                    There are many reasons why having access to your ZESA token purchase history is valuable:
                </p>
                <ul class="text-3">
                    <li class="py-1">
                        <strong>Recover Lost Tokens:</strong> If you misplaced or accidentally deleted your ZESA token SMS, you can instantly retrieve it here instead of contacting ZETDC or waiting in queues.
                    </li>
                    <li class="py-1">
                        <strong>Track Your Electricity Spending:</strong> See how much you've been spending on electricity each month so you can budget smarter and <router-link to="/maximise-your-zesa-savings"><strong>maximise your ZESA savings</strong></router-link>.
                    </li>
                    <li class="py-1">
                        <strong>Verify Your Purchases:</strong> Confirm that every token you paid for was actually delivered — complete with the amount, date, and token number.
                    </li>
                    <li class="py-1">
                        <strong>Know Your Tariff Position:</strong> By seeing how many units you've bought this month, you can use our <router-link to="/calculators/zesa-calculator"><strong>ZESA Calculator</strong></router-link> to determine which tariff band you're in before your next purchase.
                    </li>
                </ul>
            </div>
        </div>

        <div class="bg-primary text-white rounded p-4 mx-2 mb-4 text-center">
            <h3 class="text-6 font-weight-bold text-white mb-2">Never Lose a ZESA Token Again</h3>
            <p class="text-3 mb-3">
                Every token you buy through Magetsi is permanently saved in your history. No more searching through old text messages or calling customer service. Buy on Magetsi once, and your tokens are stored forever.
            </p>
            <div class="d-flex flex-column flex-sm-row justify-content-center">
                <router-link to="/zesa" class="btn btn-light btn-sm font-weight-bold shadow-none mb-2 mb-sm-0 mr-sm-2">
                    <i class="fas fa-bolt mr-1 text-warning"></i> Buy ZESA Online
                </router-link>
                <a href="https://api.whatsapp.com/send/?phone=263785832033&text=Hi+Magetsi" target="_blank" class="btn btn-success btn-sm font-weight-bold shadow-none">
                    <i class="fab fa-whatsapp mr-1"></i> Buy on WhatsApp
                </a>
            </div>
        </div>

        <div class="px-2 py-3 text-justify">
            <div class="my-auto mx-2">
                <h2 class="text-7 text-black mb-4">
                    Why Buy ZESA on Magetsi?
                </h2>
                <p class="text-3">
                    Thousands of Zimbabweans trust Magetsi for their ZESA token purchases. Here's what makes us different:
                </p>
                <div class="row">
                    <div class="col-md-6">
                        <ul class="text-3">
                            <li class="py-1">
                                <strong>Instant Token Delivery:</strong> Your ZESA token is delivered via SMS and WhatsApp within seconds of payment.
                            </li>
                            <li class="py-1">
                                <strong>Multiple Payment Options:</strong> Pay with EcoCash, VISA, or Mastercard — whatever works for you.
                            </li>
                            <li class="py-1">
                                <strong>Token History:</strong> Every purchase is permanently saved. Use this ZESA View Token tool anytime to retrieve past tokens.
                            </li>
                        </ul>
                    </div>
                    <div class="col-md-6">
                        <ul class="text-3">
                            <li class="py-1">
                                <strong>24/7 Customer Support:</strong> Our team is always available to help you with any issues, day or night.
                            </li>
                            <li class="py-1">
                                <strong>Buy Anytime, Anywhere:</strong> Purchase tokens from the comfort of your home via our website or the <a href="https://api.whatsapp.com/send/?phone=263785832033&text=Hi+Magetsi" target="_blank">Magetsi WhatsApp chatbot</a>.
                            </li>
                            <li class="py-1">
                                <strong>Safe &amp; Secure:</strong> Every transaction is protected with bank-grade encryption powered by Stripe.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <div class="px-2 py-3 text-justify">
            <div class="my-auto mx-2">
                <h2 class="text-7 text-black mb-4">
                    What Is a ZESA Token?
                </h2>
                <p class="text-3">
                    A ZESA token is a 20-digit number that you enter into your Zimbabwe Electricity (ZETDC) prepaid meter to load electricity units. When you purchase electricity, ZETDC generates this unique token which converts into the kilowatt-hours (kWh) you've paid for. Once entered into your meter, the units are credited and you can use electricity until those units are consumed.
                </p>
                <p class="text-3">
                    With Magetsi, your token is delivered instantly after payment — and it's always available here in your ZESA View Token history, so you never have to worry about losing it.
                </p>
            </div>
        </div>

        <div class="px-2 py-3 text-justify">
            <div class="my-auto mx-2">
                <h2 class="text-7 text-black mb-4">
                    How To Buy ZESA Tokens Online
                </h2>
                <p class="text-3">
                    Buying ZESA on Magetsi takes less than 2 minutes:
                </p>
                <ol class="text-3">
                    <li class="py-1">
                        Go to the <router-link to="/zesa"><strong>Buy ZESA</strong></router-link> page on Magetsi.
                    </li>
                    <li class="py-1">
                        Enter your meter number — your meter name and address will be verified automatically.
                    </li>
                    <li class="py-1">
                        Enter the amount you'd like to spend. Not sure how many units you'll get? Use the <router-link to="/calculators/zesa-calculator"><strong>ZESA Calculator</strong></router-link> first.
                    </li>
                    <li class="py-1">
                        Choose your payment method — EcoCash, VISA, or Mastercard.
                    </li>
                    <li class="py-1">
                        Complete payment and receive your token instantly via SMS. You can also view it anytime right here using the ZESA View Token tool.
                    </li>
                </ol>
            </div>
        </div>

        <div class="px-2 py-3 text-justify">
            <div class="my-auto mx-2">
                <h2 class="text-7 text-black mb-4">
                    Frequently Asked Questions
                </h2>
                <div class="accordion accordion-alternate" id="viewTokenFAQ">
                    <div class="card">
                        <div class="card-header" id="faq1">
                            <h5 class="mb-0">
                                <a href="#" class="collapsed" data-toggle="collapse"
                                   data-target="#faqCollapse1" aria-expanded="false" aria-controls="faqCollapse1">
                                    How do I view my ZESA token?
                                </a>
                            </h5>
                        </div>
                        <div id="faqCollapse1" class="collapse" aria-labelledby="faq1" data-parent="#viewTokenFAQ">
                            <div class="card-body text-3">
                                Simply enter your ZESA prepaid meter number in the tool at the top of this page and click "View My Tokens". Your complete purchase history from Magetsi will be displayed instantly, showing every token number, the amount paid, and when it was purchased.
                            </div>
                        </div>
                    </div>
                    <div class="card">
                        <div class="card-header" id="faq2">
                            <h5 class="mb-0">
                                <a href="#" class="collapsed" data-toggle="collapse"
                                   data-target="#faqCollapse2" aria-expanded="false" aria-controls="faqCollapse2">
                                    Can I view tokens purchased from other platforms?
                                </a>
                            </h5>
                        </div>
                        <div id="faqCollapse2" class="collapse" aria-labelledby="faq2" data-parent="#viewTokenFAQ">
                            <div class="card-body text-3">
                                This tool only shows ZESA tokens purchased through the Magetsi platform — either via our website (magetsi.co.zw) or our WhatsApp chatbot. Tokens purchased from ZETDC offices, banks, or other third-party vendors will not appear here. However, when you start buying through Magetsi, all your future tokens will be securely stored and accessible anytime.
                            </div>
                        </div>
                    </div>
                    <div class="card">
                        <div class="card-header" id="faq3">
                            <h5 class="mb-0">
                                <a href="#" class="collapsed" data-toggle="collapse"
                                   data-target="#faqCollapse3" aria-expanded="false" aria-controls="faqCollapse3">
                                    I lost my ZESA token. How do I get it back?
                                </a>
                            </h5>
                        </div>
                        <div id="faqCollapse3" class="collapse" aria-labelledby="faq3" data-parent="#viewTokenFAQ">
                            <div class="card-body text-3">
                                If you purchased the token through Magetsi, just enter your meter number in the tool above and your token will be displayed. If you purchased it elsewhere, you'll need to contact that vendor or visit ZETDC. Going forward, buying on Magetsi ensures you'll always have a backup of your tokens accessible right here.
                            </div>
                        </div>
                    </div>
                    <div class="card">
                        <div class="card-header" id="faq4">
                            <h5 class="mb-0">
                                <a href="#" class="collapsed" data-toggle="collapse"
                                   data-target="#faqCollapse4" aria-expanded="false" aria-controls="faqCollapse4">
                                    Is there a limit to how far back I can view tokens?
                                </a>
                            </h5>
                        </div>
                        <div id="faqCollapse4" class="collapse" aria-labelledby="faq4" data-parent="#viewTokenFAQ">
                            <div class="card-body text-3">
                                The tool shows your most recent 15 token purchases made through Magetsi. This covers a substantial history for most users. If you need to go further back, you can contact our <a href="/contact-us">customer support team</a> for assistance.
                            </div>
                        </div>
                    </div>
                    <div class="card">
                        <div class="card-header" id="faq5">
                            <h5 class="mb-0">
                                <a href="#" class="collapsed" data-toggle="collapse"
                                   data-target="#faqCollapse5" aria-expanded="false" aria-controls="faqCollapse5">
                                    Do I need an account to view my ZESA tokens?
                                </a>
                            </h5>
                        </div>
                        <div id="faqCollapse5" class="collapse" aria-labelledby="faq5" data-parent="#viewTokenFAQ">
                            <div class="card-body text-3">
                                No account, login, or registration is needed. Just enter your meter number and your token history appears instantly. Buying ZESA on Magetsi is equally hassle-free — no sign-up required to make a purchase either.
                            </div>
                        </div>
                    </div>
                    <div class="card">
                        <div class="card-header" id="faq6">
                            <h5 class="mb-0">
                                <a href="#" class="collapsed" data-toggle="collapse"
                                   data-target="#faqCollapse6" aria-expanded="false" aria-controls="faqCollapse6">
                                    What are the current ZESA tariffs?
                                </a>
                            </h5>
                        </div>
                        <div id="faqCollapse6" class="collapse" aria-labelledby="faq6" data-parent="#viewTokenFAQ">
                            <div class="card-body text-3">
                                ZESA tariffs are updated regularly by ZETDC. You can view the latest tariffs on our <router-link to="/zesa-tariffs"><strong>ZESA Tariffs</strong></router-link> page, or use the <router-link to="/calculators/zesa-calculator"><strong>ZESA Calculator</strong></router-link> to see exactly how many units your money will get you at the current rates.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="px-2 py-4 text-justify">
            <div class="my-auto mx-2">
                <h3 class="text-5 text-black mb-3">
                    Helpful ZESA Tools on Magetsi
                </h3>
                <div class="row">
                    <div class="col-sm-6 col-lg-4 mb-3">
                        <div class="card h-100 shadow-sm">
                            <div class="card-body text-center">
                                <i class="fas fa-bolt text-warning fa-2x mb-2"></i>
                                <h6 class="font-weight-bold">Buy ZESA</h6>
                                <p class="text-2 text-muted">Purchase ZESA tokens instantly with EcoCash, VISA or Mastercard.</p>
                                <router-link to="/zesa" class="btn btn-primary btn-sm shadow-none">Buy Now</router-link>
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-6 col-lg-4 mb-3">
                        <div class="card h-100 shadow-sm">
                            <div class="card-body text-center">
                                <i class="fas fa-calculator text-primary fa-2x mb-2"></i>
                                <h6 class="font-weight-bold">ZESA Calculator</h6>
                                <p class="text-2 text-muted">Calculate exactly how many units your money will buy.</p>
                                <router-link to="/calculators/zesa-calculator" class="btn btn-outline-primary btn-sm shadow-none">Calculate</router-link>
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-6 col-lg-4 mb-3">
                        <div class="card h-100 shadow-sm">
                            <div class="card-body text-center">
                                <i class="fas fa-chart-bar text-info fa-2x mb-2"></i>
                                <h6 class="font-weight-bold">ZESA Tariffs</h6>
                                <p class="text-2 text-muted">View the latest ZETDC tariff rates and consumption bands.</p>
                                <router-link to="/zesa-tariffs" class="btn btn-outline-info btn-sm shadow-none">View Tariffs</router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="px-2 pb-3 text-justify">
            <div class="my-auto mx-2 text-center">
                <p class="text-3 text-muted" style="font-size: 0.8rem;">
                    <strong>Disclaimer:</strong> The ZESA View Token tool displays purchase history from the Magetsi platform only. Tokens purchased through ZETDC offices, banks, EcoCash direct, or other third-party vendors are not included. For those tokens, please contact the original vendor or visit <a href="https://selfservice.zetdc.co.zw" target="_blank" rel="noopener noreferrer">ZETDC Self Service</a>.
                </p>
            </div>
        </div>
    </div>
</template>

<style scoped>
</style>
